import { normalizeLanguagePairValue, splitLanguagePair, stripProofreadLabel } from '../../utils/languagePairs';
import {
  getSavedProofreadTextForRow,
  hasPendingProofreadReviewForRow as sharedHasPendingProofreadReviewForRow,
  hasSavedProofreadForRow,
} from '../../utils/proofreadState';
import type { TranslationStatus } from '../../types';

export const EDIT_LOCK_TYPE_LABELS: Record<string, string> = {
  translation: 'translation',
  proofread: 'proofread',
};

export const EDIT_BLOCKING_JOB_TYPES = new Set(Object.keys(EDIT_LOCK_TYPE_LABELS));

export const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'failed',
  'error',
  'expired',
  'interrupted',
  'released',
  'cancelled',
  'canceled',
  'aborted',
  'succeeded',
  'finished',
]);

export const MIN_LOCK_ESTIMATE_MINUTES = 10;

const sanitizeExportFilenamePart = (value: string): string =>
  value.replace(/\s+/g, '_').replace(/[^\w-]/g, '');

export const filterActiveChapterEditLocks = (
  translations: TranslationStatus[],
  chapterGuid?: string | null,
): TranslationStatus[] => {
  if (!chapterGuid) return [];
  return (translations || []).filter((job: TranslationStatus) => {
    if (!job || job.chapterGuid !== chapterGuid) return false;
    const typeKey = (job?.type || '').toString().trim().toLowerCase();
    if (!EDIT_BLOCKING_JOB_TYPES.has(typeKey)) return false;
    const statusKey = (job?.status || '').toString().trim().toLowerCase();
    return !(statusKey && TERMINAL_JOB_STATUSES.has(statusKey));
  });
};

export const resolveChapterEditLockState = (
  activeEditLocks: TranslationStatus[],
): { editLocked: boolean; reason?: string } => {
  if (!activeEditLocks.length) return { editLocked: false };
  const labels = Array.from(
    new Set(
      activeEditLocks.map(job => {
        const typeKey = (job?.type || '').toString().trim().toLowerCase();
        return EDIT_LOCK_TYPE_LABELS[typeKey] || 'translation/proofread';
      }),
    ),
  );
  return {
    editLocked: true,
    reason: `Please wait for the pending ${labels.join(', ')} to finish before editing this chapter.`,
  };
};

export const hasActiveProofreadForVersion = (
  activeEditLocks: TranslationStatus[],
  bookId: string,
  version: string,
): boolean => {
  const selectedVersion = normalizeLanguagePairValue(version).toLowerCase();
  return activeEditLocks.some(job =>
    job.type?.trim().toLowerCase() === 'proofread'
    && (!job.bookId || job.bookId === bookId)
    && normalizeLanguagePairValue(job.version || job.targetLang).toLowerCase() === selectedVersion,
  );
};

export const ensureMinimumLockEstimate = (minutes: number): number => {
  if (!Number.isFinite(minutes)) return MIN_LOCK_ESTIMATE_MINUTES;
  const normalized = Math.ceil(Math.max(0, minutes));
  if (normalized === 0) return MIN_LOCK_ESTIMATE_MINUTES;
  return Math.max(MIN_LOCK_ESTIMATE_MINUTES, normalized);
};

export const computeTranslationEstimatedMinutes = (wordCount?: number): number => {
  const count = Math.max(0, wordCount || 0);
  return ensureMinimumLockEstimate(Math.floor(count / 500) * 3);
};

export const estimateExportMinutes = (chapterCount?: number): number => {
  const count = Math.max(0, chapterCount || 0);
  if (count <= 0) return 5;
  return Math.max(2, Math.min(45, Math.floor(2 + count * 0.2)));
};

export const resolveExportFilename = (title: string, version: string, format: string): string => {
  const baseTitle = sanitizeExportFilenamePart(title || 'book');
  let suffix = baseTitle;
  if (version === 'Proofread') {
    suffix = `${baseTitle}_Proofread`;
  } else if (version && version !== 'Original') {
    const { sourceLang, targetLang } = splitLanguagePair(stripProofreadLabel(version));
    if (sourceLang && targetLang) {
      suffix = `${baseTitle}_${sourceLang}_${targetLang}`;
    }
    if (version.includes('Proofread')) {
      suffix = `${suffix}_Proofread`;
    }
  }
  const extension = format === 'Word' ? 'docx' : format.toLowerCase();
  return `${suffix}.${extension}`;
};

export const buildOptimisticTranslationStatus = ({
  bookId,
  userId,
  sourceLanguage,
  targetLanguage,
  chapter,
  useBatch,
  batchInlineCount,
  now,
}: {
  bookId: string;
  userId: string;
  sourceLanguage: string;
  targetLanguage: string;
  chapter: { chapter_guid: string; chapter_name: string; word_count?: number };
  useBatch: boolean;
  batchInlineCount?: number;
  now?: number;
}): TranslationStatus => {
  const stamp = now ?? Date.now();
  const timestamp = new Date(stamp).toISOString();
  const lockGuid = `Temp-${bookId}-${targetLanguage}-${chapter.chapter_guid}-${stamp}`;
  const optimistic: TranslationStatus = {
    lockGuid,
    chapterGuid: chapter.chapter_guid,
    chapterName: chapter.chapter_name,
    sourceLang: sourceLanguage,
    targetLang: targetLanguage,
    timestamp,
    status: 'queued',
    processingState: 'queued',
    cleared: false,
    bookId,
    userId,
    word_count: chapter.word_count,
    estimatedTimeMinutes: computeTranslationEstimatedMinutes(chapter.word_count),
    error: '',
    type: 'Translation',
    clientIntent: true,
  };
  if (useBatch) {
    (optimistic as any).batchJobId = `queued-${stamp}`;
    (optimistic as any).batchProvider = 'gemini';
    (optimistic as any).batchInlineCount = batchInlineCount || 1;
  }
  return optimistic;
};

export const isRelationshipAnalysisAlreadyInProgressMessage = (value: unknown): boolean => {
  const messageText = (value || '').toString().trim().toLowerCase();
  if (!messageText) return false;
  return (
    messageText.includes('character relationship analysis is already in progress') ||
    messageText.includes('already in progress for this book')
  );
};

export const shouldQueueBulkRelationshipAnalysis = ({
  missingChapters,
  analysisInFlight,
}: {
  missingChapters: number | null | undefined;
  analysisInFlight: boolean;
}): boolean => {
  if (analysisInFlight) return false;
  return Number(missingChapters || 0) > 0;
};

export const normalizeProofreadRequestStatus = (
  status: unknown,
): 'failed' | 'pending' | 'proofread' | 'submitted' | 'skipped' => {
  const normalized = (status || '').toString().trim().toLowerCase();
  if (normalized === 'error' || normalized === 'failed') return 'failed';
  if (['pending', 'queued', 'running', 'retrying'].includes(normalized)) return 'pending';
  if (normalized === 'skipped') return 'skipped';
  if (normalized === 'proofread') return 'proofread';
  if (normalized === 'submitted') return 'submitted';
  if (normalized === 'success') return 'proofread';
  return 'submitted';
};

export type NormalizedEditingNotesResponse = {
  status: 'failed' | 'pending' | 'proofread' | 'submitted' | 'skipped';
  message: string;
  notes?: string;
  lockGuid?: string;
  lockKey?: string;
};

export const normalizeEditingNotesResponse = (
  payload: unknown,
): NormalizedEditingNotesResponse => {
  if (Array.isArray(payload)) {
    const legacyStatus = String(payload[0] || '').trim().toLowerCase();
    const legacyMessage = String(payload[1] || '').trim();
    return {
      status: normalizeProofreadRequestStatus(legacyStatus),
      message: legacyMessage,
      notes: legacyStatus === 'success' ? legacyMessage : undefined,
    };
  }

  const data = (payload && typeof payload === 'object') ? payload as Record<string, any> : {};
  return {
    status: normalizeProofreadRequestStatus(data.status),
    message: String(data.message || '').trim(),
    notes: typeof data.notes === 'string' ? data.notes : undefined,
    lockGuid: typeof data.lockGuid === 'string' ? data.lockGuid.trim() || undefined : undefined,
    lockKey: typeof data.lockKey === 'string' ? data.lockKey.trim() || undefined : undefined,
  };
};

type ProofreadOptimisticReconciliationCallbacks = {
  updateOptimisticLockGuid: (tempGuid: string, lockGuid: string) => void;
  mergeTranslation: (lockGuid: string, updates: Partial<TranslationStatus>) => void;
  removeOptimisticTranslation: (lockGuid: string) => void;
};

export type ProofreadOptimisticReconciliationResult = {
  normalizedStatus: 'failed' | 'pending' | 'proofread' | 'submitted' | 'skipped';
  lockGuid?: string;
  lockKey?: string;
  retainedOptimistic: boolean;
};

export const reconcileProofreadOptimisticTranslation = ({
  tempLockGuid,
  status,
  lockGuid,
  lockKey,
  updateOptimisticLockGuid,
  mergeTranslation,
  removeOptimisticTranslation,
}: {
  tempLockGuid: string;
  status: unknown;
  lockGuid?: unknown;
  lockKey?: unknown;
} & ProofreadOptimisticReconciliationCallbacks): ProofreadOptimisticReconciliationResult => {
  const normalizedStatus = normalizeProofreadRequestStatus(status);
  const normalizedLockGuid = typeof lockGuid === 'string' ? lockGuid.trim() : '';
  const normalizedLockKey = typeof lockKey === 'string' ? lockKey.trim() : '';
  const hasCanonicalJob = Boolean(normalizedLockGuid || normalizedLockKey);

  if (normalizedLockGuid) {
    updateOptimisticLockGuid(tempLockGuid, normalizedLockGuid);
    if (normalizedLockKey) mergeTranslation(normalizedLockGuid, { lockKey: normalizedLockKey });
  } else if (normalizedLockKey) {
    mergeTranslation(tempLockGuid, { lockKey: normalizedLockKey });
  }

  const retainedOptimistic = hasCanonicalJob && normalizedStatus === 'pending';
  if (!retainedOptimistic) removeOptimisticTranslation(tempLockGuid);

  return {
    normalizedStatus,
    lockGuid: normalizedLockGuid || undefined,
    lockKey: normalizedLockKey || undefined,
    retainedOptimistic,
  };
};

export const summarizeProofreadCompletionStatuses = (
  proofreadStatus: Record<string, string>,
  notesStatus: Record<string, string>,
): 'failed' | 'pending' | 'success' | 'skipped' | null => {
  const combined = [
    ...Object.values(proofreadStatus || {}),
    ...Object.values(notesStatus || {}),
  ];
  if (combined.length === 0) return null;
  if (combined.includes('failed')) return 'failed';
  if (combined.includes('pending') || combined.includes('submitted')) return 'pending';
  if (combined.every(value => value === 'skipped')) return 'skipped';
  return 'success';
};

export const shouldSuppressProofreadAggregateToast = ({
  isSingle,
  didSubmitSingleProofread,
  didSubmitSingleEditingNotes,
}: {
  isSingle: boolean;
  didSubmitSingleProofread: boolean;
  didSubmitSingleEditingNotes: boolean;
}): boolean => Boolean(isSingle && (didSubmitSingleProofread || didSubmitSingleEditingNotes));

export const summarizeBulkProofreadApplyResult = ({
  appliedCount,
  removedCount,
  results,
}: {
  appliedCount: number;
  skippedCount?: number;
  removedCount?: number;
  results?: Array<{ chapterGuid: string; status: 'applied' | 'skipped' | 'error' | 'removed'; message?: string }>;
}): { level: 'success' | 'warning' | 'error' | 'info'; message: string } => {
  const normalizedResults = Array.isArray(results) ? results : [];
  const errorResults = normalizedResults.filter(item => item.status === 'error');
  const removedResults = normalizedResults.filter(item => item.status === 'removed');
  const errorCount = errorResults.length;
  const normalizedRemovedCount = Number(removedCount || removedResults.length || 0);
  const firstErrorMessage = String(errorResults[0]?.message || '').trim();
  const completedCount = appliedCount + normalizedRemovedCount;

  if (errorCount > 0 && completedCount <= 0) {
    return {
      level: 'error',
      message: firstErrorMessage
        ? `No proofread reviews were auto-applied. ${errorCount} chapter${errorCount === 1 ? '' : 's'} failed. First issue: ${firstErrorMessage}`
        : `No proofread reviews were auto-applied. ${errorCount} chapter${errorCount === 1 ? '' : 's'} failed and still need manual review.`,
    };
  }

  if (errorCount > 0) {
    return {
      level: 'warning',
      message: firstErrorMessage
        ? `Applied ${completedCount} proofread review${completedCount === 1 ? '' : 's'}. ${errorCount} chapter${errorCount === 1 ? ' still needs' : 's still need'} manual review. First issue: ${firstErrorMessage}`
        : `Applied ${completedCount} proofread review${completedCount === 1 ? '' : 's'}. ${errorCount} chapter${errorCount === 1 ? ' still needs' : 's still need'} manual review.`,
    };
  }

  return {
    level: 'success',
    message: 'Pending proofreading changes have been applied.',
  };
};

export const hasPendingProofreadReviewForRow = (row: any): boolean => {
  return sharedHasPendingProofreadReviewForRow(row);
};

export const shouldSubmitBulkProofreadForRow = (
  row: any,
  options: { requiresTranslatedContent?: boolean; hasTranslatedContent?: boolean } = {},
): boolean => {
  if (options.requiresTranslatedContent && !options.hasTranslatedContent) {
    return false;
  }
  if (!row) {
    return false;
  }
  if (sharedHasPendingProofreadReviewForRow(row)) {
    return false;
  }
  const proofreadStatus = String(row?.proofread_change_set_status || '').trim().toLowerCase();
  if (['applied', 'completed_no_changes'].includes(proofreadStatus)) {
    return false;
  }
  if (row?.proofread_at) {
    return false;
  }
  return !hasSavedProofreadForRow(row);
};

export const shouldOverwriteBulkProofreadForRow = (row: any): boolean => {
  return hasSavedProofreadForRow(row);
};

export const translatedChapterNeedsProofreadHydration = (row: any): boolean => {
  if (!row) return true;

  const translatedText =
    typeof row?.translated_text === 'string' ? row.translated_text.trim() : '';
  if (!translatedText) return true;
  if (getSavedProofreadTextForRow(row)) return false;
  if (hasPendingProofreadReviewForRow(row)) return false;

  const proofreadWordCount = Number(row?.proofread_word_count || 0);
  const proofreadStatus = String(row?.proofread_change_set_status || '').trim().toLowerCase();
  return (
    proofreadWordCount > 0
    || ['applied', 'completed_no_changes'].includes(proofreadStatus)
    || Boolean(row?.proofread_at)
  );
};
